import React, { useState, useEffect, useRef } from 'react';
import googleLogo from '../assets/google-logo.png';
// Carousel removed
import GoogleReviewsCarousel from '../components/GoogleReviewsCarousel';
import styles from './styles/HomePage.module.css';
import { Link } from 'react-router-dom';
import { FaStar, FaChevronLeft, FaChevronRight, FaMusic, FaPause, FaPlay } from 'react-icons/fa';
import { Product, Subcategory } from '../types';
import { getProductImageUrls } from '../utils';
import {
  isMusicPausedByUser,
  MUSIC_PREFERENCE_EVENT,
  saveMusicPausedPreference
} from '../utils/musicPreference';
import { BRAND } from '../data/brand';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const getSubcategoryKey = (name: string | null | undefined) =>
  (name || '').trim().toLowerCase();

const getProductImage = (product: Product) => {
  return getProductImageUrls(product)[0] || null;
};

const buildSubcategoryProductImages = (products: Product[]) =>
  products.reduce<Record<string, string>>((imagesBySubcategory, product) => {
    const key = getSubcategoryKey(product.nombre_subcategoria);
    if (!key || imagesBySubcategory[key]) return imagesBySubcategory;

    const productImage = getProductImage(product);
    if (productImage) {
      imagesBySubcategory[key] = productImage;
    }

    return imagesBySubcategory;
  }, {});

const HomePage: React.FC = () => {
  // Carousel definition removed
  const GOOGLE_REVIEWS_LINK = "https://search.google.com/local/writereview?placeid=ChIJFztmHwKrt5URCV8N3je6JEk";

  // Estado para subcategorías (Burbujas)
  const [subcategories, setSubcategories] = useState<Subcategory[]>([]);
  const [subcategoryProductImages, setSubcategoryProductImages] = useState<Record<string, string>>({});
  const bubblesContainerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const musicButtonRef = useRef<HTMLButtonElement>(null);
  const [isMusicPlaying, setIsMusicPlaying] = useState(false);
  const [musicPausedByUser, setMusicPausedByUser] = useState(isMusicPausedByUser);

  const rotateBubbles = (direction: -1 | 1) => {
    const container = bubblesContainerRef.current;
    if (!container) return;

    const maxScroll = Math.max(container.scrollWidth - container.clientWidth, 0);
    const step = Math.max(container.clientWidth * 0.7, 240);
    const isAtStart = container.scrollLeft <= 8;
    const isAtEnd = container.scrollLeft >= maxScroll - 8;

    if (direction < 0 && isAtStart) {
      container.scrollTo({ left: maxScroll, behavior: 'smooth' });
      return;
    }
    if (direction > 0 && isAtEnd) {
      container.scrollTo({ left: 0, behavior: 'smooth' });
      return;
    }

    container.scrollBy({ left: direction * step, behavior: 'smooth' });
  };

  useEffect(() => {
    const fetchHomeData = async () => {
      try {
        const [subcategoriesRes, productsRes] = await Promise.all([
          fetch(`${API_URL}/subcategorias`),
          fetch(`${API_URL}/productos`)
        ]);

        const [subcategoriesData, productsData] = await Promise.all([
          subcategoriesRes.json(),
          productsRes.json()
        ]);

        if (Array.isArray(subcategoriesData)) {
          setSubcategories(subcategoriesData);
        } else {
          console.error("Received non-array data for subcategories:", subcategoriesData);
          setSubcategories([]);
        }

        if (Array.isArray(productsData)) {
          setSubcategoryProductImages(buildSubcategoryProductImages(productsData));
        } else {
          console.error("Received non-array data for products:", productsData);
          setSubcategoryProductImages({});
        }
      } catch (err) {
        console.error("Error fetching home data:", err);
        setSubcategories([]);
        setSubcategoryProductImages({});
      }
    };

    fetchHomeData();
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    if (musicPausedByUser) {
      video.muted = true;
      setIsMusicPlaying(false);
      return;
    }

    const removeUnlockListeners = () => {
      document.removeEventListener('pointerdown', unlockMusic);
      document.removeEventListener('keydown', unlockMusic);
    };
    const startMusic = () => {
      video.muted = false;
      video.play()
        .then(() => {
          setIsMusicPlaying(true);
          removeUnlockListeners();
        })
        .catch(() => {
          // Los navegadores pueden bloquear el audio hasta la primera interacción.
          video.muted = true;
        });
    };
    function unlockMusic(event: Event) {
      if (musicButtonRef.current?.contains(event.target as Node)) return;
      startMusic();
    }

    startMusic();
    document.addEventListener('pointerdown', unlockMusic);
    document.addEventListener('keydown', unlockMusic);

    return () => {
      removeUnlockListeners();
    };
  }, [musicPausedByUser]);

  useEffect(() => {
    const syncPreference = (event: Event) => {
      const customEvent = event as CustomEvent<{ paused?: boolean }>;
      setMusicPausedByUser(customEvent.detail?.paused ?? isMusicPausedByUser());
    };
    window.addEventListener(MUSIC_PREFERENCE_EVENT, syncPreference);
    return () => window.removeEventListener(MUSIC_PREFERENCE_EVENT, syncPreference);
  }, []);

  const toggleMusic = () => {
    const video = videoRef.current;
    if (!video) return;

    if (video.muted) {
      setMusicPausedByUser(false);
      saveMusicPausedPreference(false);
      video.muted = false;
      video.play()
        .then(() => setIsMusicPlaying(true))
        .catch(() => {
          video.muted = true;
          setIsMusicPlaying(false);
        });
    } else {
      video.muted = true;
      setIsMusicPlaying(false);
      setMusicPausedByUser(true);
      saveMusicPausedPreference(true);
    }
  };

  // Solo mostramos subcategorías que tengan una imagen proveniente de un
  // producto real. Así evitamos imágenes de prueba/placeholder guardadas en la
  // subcategoría o imágenes de stock genéricas.
  const visibleSubcategories = subcategories.filter(
    (sub) => subcategoryProductImages[getSubcategoryKey(sub.nombre)]
  );

  return (
    <div className={styles['home-page']}>
      {/* 1. HERO VIDEO SECTION (Moved from below) */}
      <section className={styles['hero-video-wrapper']}>
        {/* TODO: /media/video-institucional.mp4 sigue siendo el video de la
            marca anterior. Reemplazar por uno propio o quitar la seccion. */}
        <video ref={videoRef} autoPlay muted loop playsInline preload="metadata" aria-label={`${BRAND.name} - video institucional`}>
          <source src="/media/video-institucional.mp4" type="video/mp4" />
          Tu navegador no puede reproducir este video.
        </video>
      </section>

      <button
        ref={musicButtonRef}
        type="button"
        className={`${styles['music-control']} ${isMusicPlaying ? styles['music-playing'] : ''}`}
        onClick={toggleMusic}
        aria-label={isMusicPlaying ? 'Pausar música' : 'Reproducir música'}
        aria-pressed={isMusicPlaying}
      >
        <FaMusic className={styles['music-note']} aria-hidden="true" />
        <span>{isMusicPlaying ? 'Pausar música' : 'Reproducir música'}</span>
        {isMusicPlaying ? <FaPause aria-hidden="true" /> : <FaPlay aria-hidden="true" />}
      </button>

      {/* SECCIÓN DE BURBUJAS DE SUBCATEGORÍAS */}
      <section className={styles['bubbles-section']}>
        {visibleSubcategories.length > 1 && (
          <button
            type="button"
            className={`${styles['bubbles-arrow']} ${styles['bubbles-arrow-left']}`}
            onClick={() => rotateBubbles(-1)}
            aria-label="Ver categorias anteriores"
          >
            <FaChevronLeft aria-hidden="true" />
          </button>
        )}
        <div className={styles['bubbles-scroll-container']} ref={bubblesContainerRef}>
          {visibleSubcategories.map((sub) => (
            <Link to={`/productos?sub=${sub.nombre}`} className={styles['bubble-item']} key={sub.idSubcategoria}>
              <div className={styles['bubble-image-wrapper']}>
                <img
                  src={subcategoryProductImages[getSubcategoryKey(sub.nombre)]}
                  alt={sub.nombre}
                  onError={(e) => {
                    // La imagen es de un producto real pero no cargó (URL rota).
                    // Ocultamos la burbuja para no mostrar un ícono roto ni un
                    // fallback genérico.
                    const item = e.currentTarget.closest(`.${styles['bubble-item']}`) as HTMLElement | null;
                    if (item) item.style.display = 'none';
                  }}
                />
              </div>
              <span className={styles['bubble-title']}>{sub.nombre}</span>
            </Link>
          ))}
        </div>
        {visibleSubcategories.length > 1 && (
          <button
            type="button"
            className={`${styles['bubbles-arrow']} ${styles['bubbles-arrow-right']}`}
            onClick={() => rotateBubbles(1)}
            aria-label="Ver categorias siguientes"
          >
            <FaChevronRight aria-hidden="true" />
          </button>
        )}
      </section>

      <div className={styles['home-container']}>

        {/* 1. HERO VIDEO SECTION REMOVED (Moved to top) */}

        {/* 2. SECCIÓN DE CONFIANZA (GOOGLE) */}
        <section className={styles['social-proof-section']}>
          <div className={styles['google-review-card-wrapper']}>
            <div className={styles['google-header-main']}>
              <img
                src={googleLogo}
                alt="Google Logo"
                style={{ width: '30px', height: '30px' }}
              />
              <div style={{ textAlign: 'left' }}>
                <h3>{BRAND.name}</h3>
                <div className={styles['rating-row']}>
                  <span style={{ fontWeight: 'bold', color: '#333' }}>4.9</span>
                  <div className={styles.stars}>
                    <FaStar size={14} /><FaStar size={14} /><FaStar size={14} /><FaStar size={14} /><FaStar size={14} />
                  </div>
                </div>
              </div>
            </div>

            <GoogleReviewsCarousel />

            <div style={{ marginTop: '20px' }}>
              <a
                href={GOOGLE_REVIEWS_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className={styles['btn-google']}
              >
                <span className={styles['review-cta-eyebrow']}>¡Tu opinión nos importa!</span>
                <span className={styles['review-cta-brand']}>
                  <img src={googleLogo} alt="" aria-hidden="true" />
                  Google
                </span>
                <span className={styles['review-cta-stars']} aria-hidden="true">
                  <FaStar /><FaStar /><FaStar /><FaStar /><FaStar />
                </span>
                <strong>Hacé tu reseña</strong>
              </a>
            </div>
          </div>
        </section>

      </div>
    </div>
  );
};

export default HomePage;